import {
  type MaintenanceChecklistDefinition,
  type MaintenanceChecklistField,
  maintenancePdfPlacementToPixels,
} from '@gb-ticket/shared-types';
import { useEffect, useRef, useState } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { loadMaintenancePdfDocument } from '@/products/maintenance/features/pdf/load-maintenance-pdf-document';
import { ChecklistFieldView } from '@/products/maintenance/features/protocol-renderer/checklist-field-view';

type Props = {
  pdfUrl: string;
  definition: MaintenanceChecklistDefinition;
  pageCount: number;
  selectedFieldKey: string | null;
  onSelectField: (fieldKey: string) => void;
};

export function PdfOverlayEditor({
  pdfUrl,
  definition,
  pageCount,
  selectedFieldKey,
  onSelectField,
}: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState({ width: 1, height: 1 });

  const fieldsOnPage = definition.sections.flatMap((s) =>
    s.fields.filter((f) => f.placement?.page === page),
  );

  useEffect(() => {
    let cancelled = false;
    async function render() {
      const pdf = await loadMaintenancePdfDocument(pdfUrl);
      const pdfPage = await pdf.getPage(page);
      const viewport = pdfPage.getViewport({ scale: 1.2 });
      const canvas = canvasRef.current;
      if (!canvas || cancelled) {
        return;
      }
      const context = canvas.getContext('2d');
      if (!context) {
        return;
      }
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      setPageSize({ width: viewport.width, height: viewport.height });
      await pdfPage.render({ canvasContext: context, viewport, canvas }).promise;
    }
    void render().catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [pdfUrl, page]);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span>Seite</span>
        <Select value={String(page)} onValueChange={(value) => setPage(Number(value))}>
          <SelectTrigger aria-label="Seite" className="w-20">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Array.from({ length: pageCount }, (_, i) => i + 1).map((n) => (
              <SelectItem key={n} value={String(n)}>
                {n}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="relative inline-block max-w-full overflow-auto rounded-md border bg-muted">
        <canvas ref={canvasRef} className="block max-w-full" />
        {fieldsOnPage.map((field) => {
          if (!field.placement) {
            return null;
          }
          const rect = maintenancePdfPlacementToPixels(
            field.placement,
            pageSize.width,
            pageSize.height,
          );
          const selected = field.fieldKey === selectedFieldKey;
          return (
            <button
              key={field.fieldKey}
              type="button"
              className={`absolute border-2 bg-primary/10 text-left text-xs ${selected ? 'border-primary' : 'border-dashed border-muted-foreground'}`}
              style={{
                left: rect.left,
                top: rect.top,
                width: rect.width,
                height: rect.height,
              }}
              onClick={() => onSelectField(field.fieldKey)}
            >
              {field.label}
            </button>
          );
        })}
      </div>
      {selectedFieldKey ? (
        <div className="max-w-md rounded-md border p-3">
          <SelectedFieldPreview definition={definition} fieldKey={selectedFieldKey} />
        </div>
      ) : null}
    </div>
  );
}

function SelectedFieldPreview({
  definition,
  fieldKey,
}: {
  definition: MaintenanceChecklistDefinition;
  fieldKey: string;
}) {
  let field: MaintenanceChecklistField | undefined;
  for (const section of definition.sections) {
    field = section.fields.find((f) => f.fieldKey === fieldKey);
    if (field) {
      break;
    }
  }
  if (!field) {
    return null;
  }
  return <ChecklistFieldView field={field} readOnly />;
}
